package es.subastia.data.mapper

import es.subastia.data.model.AuctionAuthorityDataModel
import es.subastia.data.model.AuctionDataModel
import es.subastia.data.model.AuctionTypeDataModel
import es.subastia.data.model.LotDataModel
import es.subastia.data.model.ProvinceDataModel
import es.subastia.domain.model.AuctionAuthorityDomainModel
import es.subastia.domain.model.AuctionDatesDomainModel
import es.subastia.domain.model.AuctionDomainModel
import es.subastia.domain.model.AuctionTypeDomainModel
import es.subastia.domain.model.LotDomainModel
import es.subastia.domain.model.ProvinceDomainModel

fun AuctionDataModel.toDomain(): AuctionDomainModel {
    val procedureTypeEnum = es.subastia.domain.model.AuctionProcedureType.fromString(type)
    val categoryEnum = category?.let { AuctionTypeDomainModel.fromString(it) }

    val displayTitle = when {
        categoryEnum == AuctionTypeDomainModel.Inmuebles -> {
            // Prefer Address, then specific title logic
            val validAddress = address?.takeIf { it.isNotBlank() && it != "-" }
            validAddress?.let {
                // Capitalize first letter of each word approx or just first char
                it.lowercase().replaceFirstChar { char -> char.titlecase() }
            } ?: title
        }

        categoryEnum == AuctionTypeDomainModel.Vehiculos -> model ?: "Vehículo (${title})"
        else -> if (title.startsWith("SUB-", ignoreCase = true)) {
            // Fallback for others if still using ID
            val typeStr = procedureTypeEnum.label.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
            val locationStr = city ?: province ?: "Ubicación desconocida"
            "$typeStr en $locationStr"
        } else title
    }

    return AuctionDomainModel(
        auctionId = auctionId,
        title = displayTitle,
        description = description,
        currentPrice = currentPrice,
        basePrice = basePrice,
        dates = AuctionDatesDomainModel(
            startDate = dateStart,
            endDate = dateEnd
        ),
        authority = authority?.let {
            AuctionAuthorityDomainModel(
                name = it.name,
                email = it.email,
                phone = it.phone
            )
        },
        location = location,
        category = categoryEnum,
        procedureType = procedureTypeEnum,
        offersCount = offersCount,
        accountExpediente = accountExpediente,
        aiSummary = aiSummary,
        appraisalValue = appraisalValue,
        auctionValue = auctionValue,
        address = address,
        code = code,
        email = email,
        fax = fax,
        model = model,
        phone = phone,
        bidStep = bidStep ?: 0.0,
        boeAnnouncement = boeAnnouncement,
        boeId = boeId,
        catastralRef = catastralRef,
        city = city,
        claimedAmount = claimedAmount,
        depositAmount = depositAmount,
        hasLots = hasLots,
        isGoodOpportunity = isGoodOpportunity,
        lotId = lotId,
        minBid = minBid,
        portalUrl = portalUrl,
        province = province?.let { ProvinceDomainModel.fromString(it) },
        status = status,
        updatedAt = updatedAt,
        lots = lots.map { it.toDomain() }
    )
}

fun AuctionAuthorityDataModel.toDomain(): AuctionAuthorityDomainModel {
    return AuctionAuthorityDomainModel(
        name = name,
        email = email,
        phone = phone
    )
}

fun AuctionTypeDataModel.toDomain(): AuctionTypeDomainModel {
    return when (this) {
        AuctionTypeDataModel.Inmuebles -> AuctionTypeDomainModel.Inmuebles
        AuctionTypeDataModel.Vehiculos -> AuctionTypeDomainModel.Vehiculos
        AuctionTypeDataModel.Otros -> AuctionTypeDomainModel.Otros
    }
}

fun AuctionTypeDomainModel.toData(): AuctionTypeDataModel {
    return when (this) {
        AuctionTypeDomainModel.Inmuebles -> AuctionTypeDataModel.Inmuebles
        AuctionTypeDomainModel.Vehiculos -> AuctionTypeDataModel.Vehiculos
        AuctionTypeDomainModel.Otros -> AuctionTypeDataModel.Otros
    }
}

fun ProvinceDataModel.toDomain(): ProvinceDomainModel {
    // Assuming enum names match exactly, using valueOf would be risky if names differ, manual map safest or if names identical iterate.
    // Since we copied the enum, names are identical.
    return ProvinceDomainModel.valueOf(this.name)
}

fun ProvinceDomainModel.toData(): ProvinceDataModel {
    return ProvinceDataModel.valueOf(this.name)
}

fun LotDataModel.toDomain(): LotDomainModel {
    return LotDomainModel(
        assetId = assetId,
        catastralRef = catastralRef,
        description = description,
        address = address,
        city = city,
        province = province,
        postcode = code,
        visitable = visitable,
        charges = charges,
        registryData = registryData,
        possessionStatus = possessionStatus,
        additionalInfo = additionalInfo,
        appraisalValue = appraisalValue,
        auctionValue = auctionValue,
        depositAmount = depositAmount,
        bidStep = bidStep,
        minBid = minBid,
        lastUpdate = lastUpdate,
        imageUrl = imageUrl,
        mapImageUrl = mapImageUrl,
        latitude = latitude,
        longitude = longitude
    )
}
